import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import {
  BlockStack,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  FormLayout,
  InlineGrid,
  Page,
  RangeSlider,
  Select,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ApiError } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { GamePreview } from '../components/GamePreview';
import {
  type CampaignListItem,
  type CampaignView,
  DEFAULT_THEME,
  type GameTypeId,
  type GameView,
  type ThemeConfig,
  type ThemePreset,
  type ThemeView,
  WHEEL_ACCENT_FALLBACK,
} from '../lib/types';

const FONT_OPTIONS = [
  { label: 'System', value: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' },
  { label: 'Serif', value: 'Georgia, Cambria, Times New Roman, serif' },
  { label: 'Monospace', value: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
];

const POSITION_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Bottom right', value: 'bottom-right' },
  { label: 'Bottom left', value: 'bottom-left' },
];

const SIZE_OPTIONS = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

function ThemeEditor({
  campaignId,
  initial,
  presets,
  gameType,
}: {
  campaignId: string;
  initial: ThemeConfig;
  presets: ThemePreset[];
  gameType: GameTypeId;
}): JSX.Element {
  const [config, setConfig] = useState<ThemeConfig>(initial);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setColor = (key: keyof ThemeConfig['colors']) => (value: string) =>
    setConfig((c) => ({ ...c, colors: { ...c.colors, [key]: value } }));

  /**
   * Setter for the optional wheel colours. Clearing the field must REMOVE the
   * key, not store an empty string — the API validates every colour as a hex
   * value, so `''` would be rejected on save instead of falling back.
   */
  const setOptionalColor = (key: 'wheelBezel' | 'wheelAccent') => (value: string) =>
    setConfig((c) => {
      const colors = { ...c.colors };
      const trimmed = value.trim();
      if (trimmed) colors[key] = trimmed;
      else delete colors[key];
      return { ...c, colors };
    });

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put<ThemeView>('/theme', { campaignId, config });
      setToast('Theme saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <InlineGrid columns={{ xs: 1, md: ['twoThirds', 'oneThird'] }} gap="400">
      <Card>
        <BlockStack gap="400">
          {error ? <ErrorState message={error} /> : null}
          {presets.length > 0 ? (
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Presets
              </Text>
              <ButtonGroup>
                {presets.map((p) => (
                  <Button key={p.key} onClick={() => setConfig(p.config)}>
                    {p.name}
                  </Button>
                ))}
              </ButtonGroup>
            </BlockStack>
          ) : null}

          <FormLayout>
            <FormLayout.Group>
              <TextField label="Primary" value={config.colors.primary} onChange={setColor('primary')} autoComplete="off" />
              <TextField label="Background" value={config.colors.background} onChange={setColor('background')} autoComplete="off" />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField label="Text" value={config.colors.text} onChange={setColor('text')} autoComplete="off" />
              <TextField label="Button" value={config.colors.button} onChange={setColor('button')} autoComplete="off" />
            </FormLayout.Group>
            <TextField label="Button text" value={config.colors.buttonText} onChange={setColor('buttonText')} autoComplete="off" />

            {/* Only shown for the wheel — these colours affect nothing else.
                Both are optional: left blank the storefront uses the primary
                colour for the frame and gold for the accents, so themes saved
                before the board redesign are unaffected. */}
            {gameType === 'spin_wheel' ? (
              <>
                <Text as="h3" variant="headingSm">
                  Spin wheel board
                </Text>
                <FormLayout.Group>
                  <TextField
                    label="Wheel frame"
                    value={config.colors.wheelBezel ?? ''}
                    onChange={setOptionalColor('wheelBezel')}
                    placeholder={config.colors.primary}
                    helpText="The studded ring around the wheel. Blank uses your primary colour."
                    autoComplete="off"
                  />
                  <TextField
                    label="Wheel accent"
                    value={config.colors.wheelAccent ?? ''}
                    onChange={setOptionalColor('wheelAccent')}
                    placeholder={WHEEL_ACCENT_FALLBACK}
                    helpText="Studs, centre hub and pointer. Blank uses gold."
                    autoComplete="off"
                  />
                </FormLayout.Group>
              </>
            ) : null}

            <Select
              label="Font"
              options={FONT_OPTIONS}
              value={config.fontFamily}
              onChange={(value) => setConfig((c) => ({ ...c, fontFamily: value }))}
            />
            <FormLayout.Group>
              <Select
                label="Popup position"
                options={POSITION_OPTIONS}
                value={config.popup.position}
                onChange={(value) =>
                  setConfig((c) => ({ ...c, popup: { ...c.popup, position: value as ThemeConfig['popup']['position'] } }))
                }
              />
              <Select
                label="Popup size"
                options={SIZE_OPTIONS}
                value={config.popup.size}
                onChange={(value) =>
                  setConfig((c) => ({ ...c, popup: { ...c.popup, size: value as ThemeConfig['popup']['size'] } }))
                }
              />
            </FormLayout.Group>
            <RangeSlider
              label={`Corner radius (${config.borderRadius}px)`}
              min={0}
              max={48}
              value={config.borderRadius}
              onChange={(value) =>
                setConfig((c) => ({ ...c, borderRadius: Array.isArray(value) ? value[0] : value }))
              }
            />
            <Checkbox
              label="Confetti on win"
              checked={config.confetti}
              onChange={(checked) => setConfig((c) => ({ ...c, confetti: checked }))}
            />
          </FormLayout>

          <Button variant="primary" loading={saving} onClick={() => void save()}>
            Save theme
          </Button>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Live preview
          </Text>
          <Text as="p" tone="subdued" variant="bodySm">
            Showing the {gameType.replace('_', ' ')} as customers will see it.
          </Text>
          <GamePreview gameType={gameType} config={config} />
        </BlockStack>
      </Card>
    </InlineGrid>
    {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </>
  );
}

export function ThemesPage(): JSX.Element {
  const navigate = useNavigate();
  const campaigns = useApi(() => apiClient.get<CampaignListItem[]>('/campaigns?limit=50'), []);
  const presets = useApi(() => apiClient.get<ThemePreset[]>('/theme/presets'), []);
  const [campaignId, setCampaignId] = useState<string>('');

  const loadTheme = async (id: string): Promise<ThemeConfig> => {
    try {
      const view = await apiClient.get<ThemeView>(`/theme?campaignId=${encodeURIComponent(id)}`);
      return view.config;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return DEFAULT_THEME;
      throw err;
    }
  };

  const loadGameType = async (id: string): Promise<GameTypeId> => {
    const campaign = await apiClient.get<CampaignView>(`/campaigns/${id}`);
    if (!campaign.gameId) return 'spin_wheel';
    try {
      const game = await apiClient.get<GameView>(`/games/${campaign.gameId}`);
      return game.type;
    } catch {
      return 'spin_wheel';
    }
  };

  const selectedId = campaignId || campaigns.data?.[0]?.id || '';
  const theme = useApi(
    () => (selectedId ? loadTheme(selectedId) : Promise.resolve(DEFAULT_THEME)),
    [selectedId],
  );
  const gameType = useApi(
    () => (selectedId ? loadGameType(selectedId) : Promise.resolve<GameTypeId>('spin_wheel')),
    [selectedId],
  );

  return (
    <Page title="Themes" subtitle="Customize how your games look on the storefront">
      {campaigns.loading ? <LoadingState label="Loading campaigns" /> : null}
      {campaigns.error ? <ErrorState message={campaigns.error.message} onRetry={campaigns.refetch} /> : null}

      {campaigns.data && campaigns.data.length === 0 ? (
        <Card>
          <EmptyStatePanel
            heading="Create a campaign first"
            primaryAction={{ content: 'Create campaign', onAction: () => navigate('/campaigns') }}
          >
            <p>Themes apply to a campaign&apos;s game. Create a campaign to customize its look.</p>
          </EmptyStatePanel>
        </Card>
      ) : null}

      {campaigns.data && campaigns.data.length > 0 ? (
        <BlockStack gap="400">
          <Card>
            <Select
              label="Campaign"
              options={campaigns.data.map((c) => ({ label: c.campaignName, value: c.id }))}
              value={selectedId}
              onChange={setCampaignId}
            />
          </Card>
          {theme.loading ? <LoadingState label="Loading theme" /> : null}
          {theme.data ? (
            <ThemeEditor
              key={selectedId}
              campaignId={selectedId}
              initial={theme.data}
              presets={presets.data ?? []}
              gameType={gameType.data ?? 'spin_wheel'}
            />
          ) : null}
        </BlockStack>
      ) : null}
    </Page>
  );
}
